import moment from 'moment';
import * as Rx from 'rxjs';
import { catchError, debounceTime, map, switchMap, take, tap } from 'rxjs/operators';
import { EDateTypes } from 'v2/components/Pages/Sales/General/Filter/interfaces';

import type IFilter from 'v2/components/Pages/Sales/General/Filter/interfaces';
import type { IInvoice } from 'v2/interfaces/invoice';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import type { IProductSales } from 'v2/interfaces/product';
import type { ISalesDetails } from 'v2/interfaces/sales';

import selectAffiliateService from '../affiliate/selectAffiliate';
import apiService from '../api';

import type { SelectAffiliateService } from '../affiliate/selectAffiliate';
import type { ApiService } from '../api';

export enum EType {
  SCROLL = 'scroll',
  PAGINATION = 'pagination',
}

const today = moment().format('DD/MM/YYYY');

export class SalesService {
  private invoices$ = new Rx.BehaviorSubject<IInvoice[]>([]);
  private totalItems$ = new Rx.BehaviorSubject<number>(0);
  private totalCurrency$ = new Rx.BehaviorSubject<number>(0);
  private loading$ = new Rx.BehaviorSubject<boolean>(false);
  private filter$ = new Rx.BehaviorSubject<IFilter>({ date_type: EDateTypes.CREATED, date: [today, today], affiliates: [] });
  private showAdvancedResult$ = new Rx.BehaviorSubject<boolean>(false);
  private filterIsAdvanced$ = new Rx.BehaviorSubject<boolean>(false);
  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>({ current_page: 1, per_page: 50 });
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);
  private type$ = new Rx.BehaviorSubject<EType>(EType.PAGINATION);
  private dateRef$ = new Rx.BehaviorSubject<EDateTypes>(EDateTypes.CREATED);
  private affiliates$ = new Rx.BehaviorSubject<any[]>([]);
  private products$ = new Rx.BehaviorSubject<IProductSales[]>([]);
  private loadingProducts$ = new Rx.BehaviorSubject<boolean>(false);
  private coupons$ = new Rx.BehaviorSubject<any[]>([]);
  private loadingCoupons$ = new Rx.BehaviorSubject<boolean>(false);
  private isTagsVisible$ = new Rx.BehaviorSubject<boolean>(true);

  constructor(private readonly apiService: ApiService, private readonly selectAffiliateService: SelectAffiliateService) {}

  public getInvoices = (): Rx.Observable<IInvoice[]> => this.invoices$;

  public getTotalItems = (): Rx.Observable<number> => this.totalItems$;

  public getTotalCurrency = (): Rx.Observable<number> => this.totalCurrency$;

  public getFilter = (): Rx.Observable<IFilter> => this.filter$;

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public getType = (): Rx.Observable<EType> => this.type$;

  public getDateRef = (): Rx.Observable<EDateTypes> => this.dateRef$;

  public getAffiliates = (): Rx.Observable<any[]> => this.affiliates$;

  public getProducts = (): Rx.Observable<IProductSales[]> => this.products$;

  public getCoupons = (): Rx.Observable<any[]> => this.coupons$;

  public getLoadingCoupons = (): Rx.Observable<boolean> => this.loadingCoupons$;

  public getFilterIsAdvanced = (): Rx.Observable<boolean> => this.filterIsAdvanced$;

  public getShowAdvancedResult = (): Rx.Observable<boolean> => this.showAdvancedResult$;

  public getTagsVisible = (): Rx.Observable<boolean> => this.isTagsVisible$;

  isLoading = () => this.loading$;

  isLoadingProducts = () => this.loadingProducts$;

  public formatFilter = (filter: Partial<IFilter>) => {
    const newFilter: Partial<Omit<IFilter, 'affiliates'>> & { only_affiliate?: boolean; only_producers?: boolean } = {};
    if (filter.date_type) {
      if (filter.date) {
        const [startDate, endDate] = filter.date;
        newFilter[filter.date_type] = startDate;
        newFilter[`${filter.date_type}_final`] = endDate;
      }
      newFilter.orderby = `${filter.date_type}-desc`;
    }
    newFilter.invoice_id = filter.invoice_id;
    if (Array.isArray(filter.invoice_id) && !filter.invoice_id.length) {
      delete newFilter.invoice_id;
    }
    if (Array.isArray(filter.payment_status) && filter.payment_status.length) {
      newFilter.payment_status = filter.payment_status.filter((status) => status !== 'partial_refund');
      if (filter.payment_status.includes('partial_refund')) {
        newFilter.refund_type = 'partial';
      }
    }
    if (filter.customer) {
      newFilter[filter.customer_type] = filter.customer;
    }
    if (Array.isArray(filter.product_id) && filter.product_id.length) {
      newFilter.product_id = filter.product_id;
    }
    if (Array.isArray(filter.charge_type) && filter.charge_type.length) {
      newFilter.charge_type = filter.charge_type;
    }
    if (Array.isArray(filter.payment_method) && filter.payment_method.length) {
      newFilter.payment_method = filter.payment_method;
    }
    if (Array.isArray(filter.affiliates) && filter.affiliates.length) {
      const selectedAffiliatesId = filter.affiliates.map((affiliate) => affiliate.id);
      const affiliates = this.selectAffiliateService.getAffiliates().value;
      const isAllAffiliatesSelected = affiliates.length === selectedAffiliatesId.length;
      if (isAllAffiliatesSelected) {
        newFilter.only_affiliate = true;
      } else {
        newFilter.affiliate_id = selectedAffiliatesId;
      }
    }
    if (Array.isArray(filter.type) && filter.type.length) {
      newFilter.type = filter.type;
    }
    newFilter.contract_id = filter.contract_id;
    if (Array.isArray(filter.contract_id) && !filter.contract_id.length) {
      delete newFilter.contract_id;
    }
    newFilter.utms = filter.utms;
    if (Array.isArray(filter.utms) && !filter.utms.length) {
      delete newFilter.utms;
    }
    if (filter.courtesy_recovery) {
      newFilter.courtesy_recovery = filter.courtesy_recovery;
    }
    if (filter.recovery) {
      newFilter.recovery = filter.recovery;
    }
    if (filter.order_bump) {
      newFilter.order_bump = filter.order_bump;
    }
    if (filter.chargeback) {
      newFilter.chargeback = filter.chargeback;
    }
    if (filter.unlimited_installment) {
      newFilter.unlimited_installment = filter.unlimited_installment;
    }
    if (filter.infinity_funnel) {
      newFilter.infinity_funnel = filter.infinity_funnel;
    }
    if (filter.fast_sale) {
      newFilter.fast_sale = filter.fast_sale;
    }
    if (filter.upsell) {
      newFilter.upsell = filter.upsell;
    }
    if (filter.one_click_buy) {
      newFilter.one_click_buy = filter.one_click_buy;
    }
    newFilter.coupon_id = filter.coupon_id;
    if (Array.isArray(filter.coupon_id) && !filter.coupon_id.length) {
      delete newFilter.coupon_id;
    }
    if (filter.origin_sale === 'affiliate') {
      newFilter.only_affiliate = true;
      newFilter.only_producers = undefined;
    }
    if (filter.origin_sale === 'producer') {
      newFilter.only_producers = true;
      newFilter.only_affiliate = undefined;
    }
    return newFilter;
  };

  public setFilterIsAdvanced = (state: boolean): void => this.filterIsAdvanced$.next(state);

  public setShowAdvancedResult = (state: boolean): void => this.showAdvancedResult$.next(state);

  public setFilter(filter: IFilter): void {
    this.filter$.pipe(take(1)).subscribe((currentFilter: IFilter) => {
      this.filter$.next({ ...currentFilter, ...filter });
      this.showAdvancedResult$.next(filter.advanced);
      this.dateRef$.next(filter.date_type);
    });
  }

  public resetPage() {
    this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });

    return this.getPaginationParams();
  }

  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next(pagination);
  }

  public setProducts = (products: any[]) => {
    this.products$.next(products);
  };

  public loadProducts() {
    this.apiService
      .get('reports/sales-filter-products')
      .pipe(
        tap(() => this.loadingProducts$.next(true)),
        map((response) => response.data),
        tap(() => this.loadingProducts$.next(false))
      )
      .subscribe((products: any) => this.products$.next(products));
  }

  public loadAffiliates() {
    this.apiService
      .get('reports/affiliates')
      .pipe(map((response) => response.data.map((a: any) => ({ label: a.name, value: a.id }))))
      .subscribe((affiliates: any) => this.affiliates$.next(affiliates));
  }

  public loadCoupons() {
    this.loadingCoupons$.next(true);
    this.apiService
      .get('/reports/coupons')
      .pipe(
        map((response) => response.data.map((a: any) => ({ label: a.key, value: a.id }))),
        tap((coupons) => this.coupons$.next(coupons)),
        tap(() => this.loadingCoupons$.next(false))
      )
      .subscribe();
  }

  public load() {
    return this.getFilter().pipe(
      map((f) => this.formatFilter(f)),
      switchMap((filter) => {
        return this.resetPage().pipe(map((params) => ({ ...filter, ...params })));
      }),
      tap(() => this.loading$.next(true)),
      debounceTime(10),
      switchMap((filter) => this.apiService.post('reports/sales', filter)),
      tap((invoices) => this.invoices$.next(invoices.data)),
      tap((invoices) => this.totalCurrency$.next(invoices.total_gain)),
      tap((invoices) => this.paginationResponse$.next(invoices.pagination)),
      tap((invoices) => this.totalItems$.next(invoices.pagination.total_items)),
      tap(() => this.loading$.next(false)),
      catchError(() => {
        this.loading$.next(false);
        this.invoices$.next([]);
        this.totalItems$.next(0);
        this.paginationResponse$.next(null);
        return Rx.of([]);
      })
    );
  }

  public simpleExport() {
    return this.apiService.post('reports/sales/export', this.formatFilter(this.filter$.value));
  }

  public deitaledExport() {
    return this.apiService.post('reports/sales/export/detailed', this.formatFilter(this.filter$.value));
  }

  public getSalesDetails(): Rx.Observable<ISalesDetails> {
    return this.apiService.post('reports/sales/details', this.formatFilter(this.filter$.value)).pipe(map((response) => response.data));
  }

  public exportBackground(email: string, detailed: boolean) {
    return this.apiService.post('reports/export/sales', {
      ...this.formatFilter(this.filter$.value),
      sender_email: email ? email : null,
      export_detailed: detailed,
    });
  }
  public backgroundExport(values: any) {
    return this.apiService.post('reports/background/export/sales', values);
  }

  public gainResults() {
    return this.getFilter().pipe(
      map((f) => this.formatFilter(f)),
      switchMap((filter) => {
        return this.resetPage().pipe(map((params) => ({ ...filter, ...params })));
      }),
      tap(() => this.loading$.next(true)),
      debounceTime(10),
      switchMap((filter) => this.apiService.get('reports/sales/gains', filter))
    );
  }

  public cleanFilter() {
    this.filter$.next({ date_type: EDateTypes.CREATED, date: [today, today], affiliates: [] });
  }

  public toggleTagsVisibility() {
    this.isTagsVisible$.next(!this.isTagsVisible$.value);
  }
}

const salesService = new SalesService(apiService, selectAffiliateService);
export default salesService;
